import { CanadaPostManifestMode } from "@redotech/redo-fulfillment-carrier-account-common/canada-post";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import {
  nonEmptyValidator,
  notUndefinedValidator,
} from "@redotech/ui/validators";
import { memo } from "react";
import { RedoTextInput } from "../../../../arbiter-components/input/redo-text-input";
import { RedoExternalLink } from "../../../../arbiter-components/links/redo-external-link";
import {
  RadioGroupOption,
  RedoRadioGroup,
} from "../../../../arbiter-components/radio/redo-radio-group";
import { Flex } from "../../../../flex";
import { Text } from "../../../../text";

export const CanadaPostDirectCarrierInput = groupInput({
  apiKey: input<string>({ validator: nonEmptyValidator }),
  apiSecret: input<string>({ validator: nonEmptyValidator }),
  customerNumber: input<string>({ validator: nonEmptyValidator }),
  contractNumber: input<string>({ validator: nonEmptyValidator }),
  manifestMode: input<CanadaPostManifestMode | undefined>({
    validator: notUndefinedValidator,
  }),
});

export type CanadaPostDirectCarrierValue = InputSchema.Value<
  typeof CanadaPostDirectCarrierInput
>;

export type CanadaPostDirectCarrierForm = InputSchema.Input<
  typeof CanadaPostDirectCarrierInput
>;

export const canadaPostDirectCarrierDefault =
  (): CanadaPostDirectCarrierValue => ({
    apiKey: "",
    apiSecret: "",
    customerNumber: "",
    contractNumber: "",
    manifestMode: undefined,
  });

const manifestModeOptions: RadioGroupOption<
  CanadaPostManifestMode | undefined
>[] = [
  { id: CanadaPostManifestMode.ManifestNotRequired, label: "No" },
  { id: CanadaPostManifestMode.ManifestRequired, label: "Yes" },
];

export const CreateCanadaPostDirectCarrierForm = memo(
  function CreateCanadaPostDirectCarrierForm({
    carrierInput,
  }: {
    carrierInput: CanadaPostDirectCarrierForm;
  }) {
    return (
      <Flex dir="column" gap="3xl">
        <Flex dir="column" gap="lg">
          <Text fontSize="sm" fontWeight="semibold">
            API credentials
          </Text>
          <Flex dir="column">
            <RedoTextInput
              label="API key"
              placeholder=""
              setValue={carrierInput.inputs.apiKey.setValue}
              value={carrierInput.inputs.apiKey.value}
            />
            <RedoTextInput
              inputTypeHint="password"
              label="API secret"
              placeholder=""
              setValue={carrierInput.inputs.apiSecret.setValue}
              value={carrierInput.inputs.apiSecret.value}
            />
          </Flex>
          <Flex align="flex-start" flexDirection="column" gap="xs">
            <Text color="tertiary" fontSize="xs">
              Create a Canada Post Developer Portal app and add Rating (4.0.0),
              Returns (2.0.0), and Shipping (8.0.0) before connecting it to
              Redo.
            </Text>
            <RedoExternalLink
              text="Create or manage a Canada Post app"
              url="https://developer-developpeur.canadapost-postescanada.ca/devportal-portaildesdeveloppeurs/apps"
            />
          </Flex>
        </Flex>
        <Flex dir="column" gap="lg">
          <Text fontSize="sm" fontWeight="semibold">
            Canada Post account
          </Text>
          <Flex dir="column">
            <RedoTextInput
              label="Customer number"
              placeholder=""
              setValue={carrierInput.inputs.customerNumber.setValue}
              value={carrierInput.inputs.customerNumber.value}
            />
            <RedoTextInput
              label="Contract number"
              placeholder=""
              setValue={carrierInput.inputs.contractNumber.setValue}
              value={carrierInput.inputs.contractNumber.value}
            />
          </Flex>
          <RedoExternalLink
            text="Find customer and contract number"
            url="https://www.canadapost-postescanada.ca/bam-gce/en/profile"
          />
        </Flex>
        <RedoRadioGroup<CanadaPostManifestMode | undefined>
          label="Will you manifest your packages?"
          options={manifestModeOptions}
          setValue={carrierInput.inputs.manifestMode.setValue}
          value={carrierInput.inputs.manifestMode.value}
        />
      </Flex>
    );
  },
);
